import { useContext, useEffect, useState } from "react";
import Icon from "../../components/Icon/Icon";
import styles from "./DocumentReview.module.css";
import { useNavigate, useParams } from "react-router-dom";
import SelectElement, {
  type SelectListItemType,
} from "../../components/SelectElement/SelectElement";
import type {
  IGetAllDocumentsTypesResponse,
  IUpdateTemplateCredentials,
  IUpdateTemplateFieldsCredentials,
} from "../../shared/types/templates.types";
import api from "../../api/api";
import type {
  IGetDocumentByIdResponse,
  IUpdateDocumentByIdBody,
} from "../../shared/types/documents.types";
import StatusModal from "../../components/StatusModal/StatusModal";
import CreatePropertyModal from "../../components/CreatePropertyModal/CreatePropertyModal";

import { v4 as uuidv4 } from "uuid";
import ConfirmActionModal from "../../components/ConfirmActionModal/ConfirmActionModal";
import { LoaderContext } from "../../shared/Contexts/Loader/LoaderContext";

type DocumentFieldType = IUpdateDocumentByIdBody["fields"][number] & {
  typeFieldName: string;
  typeFieldLabel: string;
  confidence: number;
  isDeleted: boolean;
  isMissing: boolean;
};

type PropertyType = {} & IUpdateTemplateFieldsCredentials;

const DocumentReview = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();

  const { isLoading } = useContext(LoaderContext);
  const [emptyFieldError, setEmptyFieldError] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>();
  const [documentFields, setDocumentFields] = useState<DocumentFieldType[]>();
  const [existingFieldsNames, setExistingFieldsNames] = useState<string[]>([]);
  const [templateData, setTemplateData] =
    useState<IUpdateTemplateCredentials | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [
    isConfirmDeletePropertyModalOpen,
    setIsConfirmDeletePropertyModalOpen,
  ] = useState(false);
  const [isSuccessDeleteModalOpen, setIsSuccessDeleteModalOpen] =
    useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [deletedFieldsNames, setDeletedFieldsNames] = useState<string[]>([]);

  const handleGetTemporaryLink = async (fileUrl: string) => {
    try {
      const link = await api.getTemporaryLinkToFile({ fileUrl });
      return link.signedUrl;
    } catch (error) {
      alert("Error while trying to get document preview link!");
      console.log(error);
    }
  };

  const [documentsTypes, setDocumentsTypes] =
    useState<IGetAllDocumentsTypesResponse | null>(null);
  const [selectedOption, setSelectedOption] = useState<SelectListItemType>({
    id: "Tipo de Documento",
    element: <>Tipo de Documento</>,
  });

  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  // fetch types
  useEffect(() => {
    const fetchDocumentsTypes = async () => {
      try {
        const data = await api.getAllDocumentsTypes();
        setDocumentsTypes(data);
      } catch (error) {
        alert("Error while trying to fetch documents types!");
        console.log(error);
      }
    };
    fetchDocumentsTypes();
  }, []);

  const [document, setDocument] = useState<IGetDocumentByIdResponse | null>(
    null
  );

  const [documentTypeId, setDocumentTypeId] = useState<string | null>(null);

  const fetchChangeType = async (id: string) => {
    if (!documentId) return;
    try {
      await api.updateDocumentTemplateById({
        documentId,
        documentTypeId: id,
      });
    } catch (error) {
      console.log(error);
    }
  };
  const selectOption = (option: SelectListItemType) => {
    setSelectedOption(option);
  };

  const handleChangeType = async (option: SelectListItemType) => {
    selectOption(option);
    await fetchChangeType(option.id);
    setDocumentTypeId(option.id);
  };

  // fetch doc
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        if (!documentId) return;

        const data = await api.getDocumentById({ id: documentId });
        setDocument(data);
        if (data?.type?.id) {
          setSelectedOption({
            id: data.type.id,
            element: <>{data.type.name}</>,
          });
        }

        const imageLink = await handleGetTemporaryLink(data.fileUrl);
        setImagePreviewUrl(imageLink);
        const documentFields: DocumentFieldType[] = data.fields.map(
          (documentField) => {
            return {
              id: documentField.id,
              typeFieldName: documentField.field.name,
              typeFieldLabel: documentField.field.label,
              value: documentField.value,
              fieldConfigId: documentField.fieldId,
              validated: documentField.validated,
              confidence: documentField.confidence,
              isDeleted: documentField.field.deletedAt !== null,
              isMissing: documentField.value === "",
            };
          }
        );
        setDocumentFields(documentFields);
      } catch (error) {
        console.log(error);
      }
    };

    fetchDocument();
  }, [documentId, documentTypeId]);

  // fetch type
  useEffect(() => {
    const fetchTemplateById = async () => {
      if (!document) return;
      if (!document.type) return;

      try {
        const template = await api.getTemplateById({ id: document.type.id });
        const templateFieldsToUpdate = template.fields.map(
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          ({ documentTypeId, deletedAt, ...field }) => {
            return {
              ...field,
              order: field.order + 1,
              //Removing filed id, etc from field for update
              keywords:
                field.keywords &&
                field.keywords.map((keyword) => {
                  return {
                    keyword: keyword.keyword,
                    id: keyword.id,
                  };
                }),
              queries:
                field.queries &&
                field.queries.map((query) => {
                  return {
                    question: query.question,
                    id: query.id,
                  };
                }),
            };
          }
        );

        const mappedDocumentTypeKeyword = template.DocumentTypeKeyword.map(
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          ({ documentTypeId, ...keywordObj }) => {
            return keywordObj;
          }
        );

        const dataToSave: IUpdateTemplateCredentials = {
          id: template.id,
          name: template.name,
          description: template.description,
          departmentId: template.departmentId,
          documentTypeKeyword: mappedDocumentTypeKeyword,
          fields: templateFieldsToUpdate,
        };

        setTemplateData(dataToSave);
        const existingFieldsNames = dataToSave.fields.map(
          (field) => field.label
        );
        setExistingFieldsNames(existingFieldsNames);
      } catch (error) {
        alert("Fetching template failed!");
        console.log(error);
      }
    };
    fetchTemplateById();
  }, [document]);

  // fetch type

  const handleChangeProperties = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    if (!document) return;
    const value = e.currentTarget.value;

    setDocumentFields((prev) => {
      if (!prev) return prev;

      const updatedFields = [...prev];
      const fieldIndex = updatedFields.findIndex((field) => field.id === id);

      if (fieldIndex !== -1) {
        updatedFields[fieldIndex] = {
          ...updatedFields[fieldIndex],
          value,
          validated: true,
        };
        return updatedFields;
      }

      return prev;
    });
  };

  const toCamelCase = (label: string) => {
    return label
      .trim()
      .split(" ")
      .map((word, index) =>
        index === 0
          ? word.toLowerCase()
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join("");
  };

  const handleCreateProperty = ({
    label,
    keywords,
  }: {
    label: string;
    keywords: { keyword: string }[];
  }) => {
    if (!document) return;
    if (!templateData) return;
    const newTypeField: PropertyType = {
      name: toCamelCase(label),
      label,
      order: document.fields.length + 1,
      isRequired: true,
      keywords: keywords.map((keywordObj) => {
        return { keyword: keywordObj.keyword };
      }),
    };

    // template changes
    setTemplateData((prev) =>
      prev
        ? {
            ...prev,
            fields: [...prev.fields, newTypeField],
          }
        : null
    );

    // document fields changes
    const generatedId = uuidv4();
    const createdField: DocumentFieldType = {
      typeFieldName: toCamelCase(label),
      typeFieldLabel: label,
      id: generatedId,
      value: "",
      isMissing: false,
      validated: true,
      fieldConfigId: "",
      confidence: 100,
      isDeleted: false,
    };

    setDocumentFields((prev) =>
      prev ? [...prev, createdField] : [createdField]
    );

    setIsModalOpen(false);
    setIsStatusModalOpen(true);
  };

  const handleDeleteProperties = () => {
    console.log(templateData);
    if (!documentFields || !templateData || !selectedFields) return;
    const updatedDocFields = documentFields.filter(
      (field) => !selectedFields.includes(field.typeFieldLabel)
    );
    setDocumentFields(updatedDocFields);

    const selectedFieldNames = documentFields
      .filter((field) => selectedFields.includes(field.typeFieldLabel))
      .map((field) => field.typeFieldLabel);

    const updatedTemplateFields = templateData.fields.filter(
      (field) => !selectedFieldNames.includes(field.label)
    );
    setTemplateData((prev) =>
      prev ? { ...prev, fields: updatedTemplateFields } : null
    );
    setDeletedFieldsNames(selectedFieldNames);
    setSelectedFields([]);
  };

  const handleSubmit = async () => {
    if (!documentFields) return;
    if (document === null) return;
    if (!document.type) return;
    if (templateData === null) return;

    try {
      console.log(templateData);
      const isSomeFieldEmpty = documentFields.some(
        (field) => field.value === ""
      );
      if (isSomeFieldEmpty) {
        setEmptyFieldError(true);
        return;
      }
      const updatedTemplate = await api.updateTemplate(templateData);
      const updatedTemplateFields = updatedTemplate.fields;
      const fieldsToSend = documentFields.map(
        ({
          typeFieldName,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          typeFieldLabel,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          confidence,
          isDeleted,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          isMissing,
          ...docField
        }) => {
          const matchingTemplateField = updatedTemplateFields.find(
            (tempField, index) => {
              if (isDeleted) return true;
              console.log(
                "index:",
                index,
                "templatefieldName: ",
                tempField.name,
                "typeFieldName:",
                typeFieldName
              );
              return tempField.name === typeFieldName;
            }
          );
          if (!matchingTemplateField) {
            throw new Error(
              `Server can not find Template field to this document field: ${typeFieldName}! Try again`
            );
          }
          return {
            ...docField,
            fieldConfigId: matchingTemplateField?.id,
          };
        }
      );

      const dataToSend = {
        fields: fieldsToSend,
        typeId: document.type.id,
      };
      console.log(dataToSend);
      api.updateDocumentById({
        params: {
          id: document.id,
        },
        body: dataToSend,
      });
      navigate("/");
    } catch (error) {
      alert("Error while trying to update document!");
      console.log(error);
    }
  };

  return (
    <>
      <div className={styles.mainContainer}>
        {document ? (
          <>
            <div className={styles.aboutDocumentContainer}>
              <div className={styles.propertiesContainer}>
                <h3 className={styles.title}>Propiedades extraídas</h3>
                <div className={styles.buttonsContainer}>
                  <button
                    className={styles.createButton}
                    type="button"
                    disabled={!templateData}
                    onClick={() => setIsModalOpen(true)}
                  >
                    Crear propiedad
                    <Icon size={22} name="edit-pen" gradient={true} />
                  </button>
                  <span className={styles.divider}></span>
                  <button
                    onClick={() => {
                      setIsConfirmDeletePropertyModalOpen(true);
                    }}
                    disabled={selectedFields.length === 0}
                    className={styles.deleteButton}
                    type="button"
                  >
                    Eliminar propiedades
                    <Icon name="trash-can" size={18} color="#FFFFFF" />
                  </button>
                </div>
                {!templateData && <p>¡Debe seleccionar un tipo!</p>}
                {documentFields && documentFields?.length > 0 && (
                  <div className={styles.propertiesListWrapper}>
                    <ul className={styles.propertiesList}>
                      {documentFields.map((documentField) => {
                        return (
                          <li
                            className={`${styles.propertiesListItem} ${
                              documentField.isDeleted === true &&
                              styles.deprecated
                            }  ${
                              documentField.isMissing === true &&
                              styles.missingField
                            }`}
                            key={documentField.id}
                          >
                            <label className={`${styles.propertyContent} `}>
                              {documentField.typeFieldLabel}
                              {documentField.isMissing && (
                                <span className={styles.missingFieldLabel}>
                                  (Falta en documento, puede editar)
                                </span>
                              )}
                              {documentField.value === "" &&
                                emptyFieldError && (
                                  <span className={styles.textWarning}>
                                    El campo no puede estar vacío.
                                  </span>
                                )}
                              <div className={styles.inputContainer}>
                                <input
                                  className={styles.customCheckbox}
                                  type="checkbox"
                                  checked={selectedFields.includes(
                                    documentField.typeFieldLabel
                                  )}
                                  onChange={() => {
                                    const label = documentField.typeFieldLabel;
                                    const isSelected =
                                      selectedFields.includes(label);

                                    const updatedFields = isSelected
                                      ? selectedFields.filter(
                                          (fieldId) => fieldId !== label
                                        )
                                      : [...selectedFields, label];

                                    setSelectedFields(updatedFields);
                                  }}
                                />
                                <input
                                  className={styles.propertyData}
                                  defaultValue={documentField.value}
                                  onChange={(e) =>
                                    handleChangeProperties(e, documentField.id)
                                  }
                                />
                                <span className={styles.confidence}>
                                  {`(${Math.floor(
                                    documentField.confidence
                                  )}% precisión)`}
                                </span>
                              </div>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
              <div className={styles.informationContainer}>
                <h4 className={styles.informationTitle}>
                  Información del Documento
                </h4>
                <ul className={styles.informationList}>
                  <li className={styles.formGroup}>
                    <label className={styles.infoLabel}>
                      Tipo de documento
                    </label>
                    {documentsTypes && (
                      <SelectElement
                        options={documentsTypes.map((type) => {
                          return {
                            id: type.id,
                            element: (
                              <p className={styles.selectListItem}>
                                {type.name}
                              </p>
                            ),
                          };
                        })}
                        setSelected={handleChangeType}
                        selected={selectedOption}
                        className={styles.selectElement}
                        dropdownItemClassName={styles.selectElementDropdownItem}
                        dropdownClassName={styles.selectElementDropdown}
                      />
                    )}
                  </li>
                  <li className={styles.formGroup}>
                    <label className={styles.infoLabel}>Departamento</label>
                    <p className={styles.infoValue}>
                      {document.type?.department?.code || "None"}
                    </p>
                    {/* {documentsTypes && (
                      <SelectElement
                        options={documentsTypes.map((type) => {
                          return {
                            id: type.id,
                            element: (
                              <p className={styles.selectListItem}>
                                {type.name}
                              </p>
                            ),
                          };
                        })}
                        setSelected={handleChangeType}
                        selected={selectedOption}
                        className={styles.selectElement}
                        dropdownItemClassName={styles.selectElementDropdownItem}
                        dropdownClassName={styles.selectElementDropdown}
                      />
                    )} */}
                  </li>
                  <li className={styles.formGroup}>
                    <label className={styles.infoLabel}>
                      Numero de expediente electronico
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={document.expedientNumber || "None"}
                      className={styles.infoValue}
                    />
                  </li>
                </ul>
              </div>
            </div>
            <div className={styles.imageContainer}>
              <h5 className={styles.imageName}>
                {document.fileName.substring(
                  0,
                  document.fileName.lastIndexOf(".")
                )}
              </h5>

              <iframe src={imagePreviewUrl} className={styles.image}></iframe>
              <a
                target="_blank"
                href={imagePreviewUrl}
                className={styles.getFullImageLink}
              >
                Ver documento completo <Icon name="full-size" size={25} />
              </a>
              <button
                disabled={documentFields?.length === 0}
                className={styles.continueButton}
                onClick={handleSubmit}
              >
                Procesar
              </button>
            </div>
          </>
        ) : (
          !isLoading && <>Datos no encontrados {":("}</>
        )}
      </div>
      <CreatePropertyModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        onCreate={handleCreateProperty}
        existingFieldsNames={existingFieldsNames}
      />
      <StatusModal
        isOpen={isStatusModalOpen}
        onRequestClose={() => {
          setIsStatusModalOpen(false);
        }}
      >
        <p className={styles.statusModalTitle}>¡Propiedad creada con éxito!</p>
        <p className={styles.statusModalText}>
          Será guardada automáticamente en el registro de propiedades
        </p>
      </StatusModal>
      <ConfirmActionModal
        isOpen={isConfirmDeletePropertyModalOpen}
        onRequestClose={() => {
          setIsConfirmDeletePropertyModalOpen(false);
        }}
        modalTitle="Eliminar Propiedad"
        modalText="¿Está seguro que desea eliminar esta propiedad?, no se extraerá esta propiedad para futuros documentos"
        cancelActionText="Cancelar"
        cancelActionHandler={() => {
          setIsConfirmDeletePropertyModalOpen(false);
        }}
        confirmActionText="Eliminar propiedad"
        confirmActionHandler={() => {
          handleDeleteProperties();
          setIsConfirmDeletePropertyModalOpen(false);
          setIsSuccessDeleteModalOpen(true);
        }}
      />
      <StatusModal
        icon={
          <Icon
            name="folder-with-trashcan"
            gradient={true}
            className={styles.modalIcon}
            size={110}
          />
        }
        isOpen={isSuccessDeleteModalOpen}
        onRequestClose={() => {
          setIsSuccessDeleteModalOpen(false);
          setDeletedFieldsNames([]);
        }}
      >
        <p className={styles.statusModalText}>
          La propiedad {deletedFieldsNames.map((field) => field).join(", ")} se
          ha eliminado
        </p>
      </StatusModal>
    </>
  );
};

export default DocumentReview;
